import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Input from '@/components/common/Input';
import Select from '@/components/common/Select';
import Button from '@/components/common/Button';
import { useCreateAccount, useUpdateAccount, useTestAccount } from '@/hooks/useAccounts';
import { useOAuth2, detectOAuth2Provider } from '@/hooks/useOAuth2';
import OAuth2ConfigPrompt from '@/components/accounts/OAuth2ConfigPrompt';

// Common email provider IMAP settings
const EMAIL_PROVIDERS = {
  'gmail.com': { host: 'imap.gmail.com', port: 993, tls: true },
  'googlemail.com': { host: 'imap.gmail.com', port: 993, tls: true },
  'outlook.com': { host: 'outlook.office365.com', port: 993, tls: true },
  'hotmail.com': { host: 'outlook.office365.com', port: 993, tls: true },
  'live.com': { host: 'outlook.office365.com', port: 993, tls: true },
  'yahoo.com': { host: 'imap.mail.yahoo.com', port: 993, tls: true },
  'icloud.com': { host: 'imap.mail.me.com', port: 993, tls: true },
  'me.com': { host: 'imap.mail.me.com', port: 993, tls: true },
  'aol.com': { host: 'imap.aol.com', port: 993, tls: true },
  'zoho.com': { host: 'imap.zoho.com', port: 993, tls: true },
};

export default function AccountForm({ account, onSuccess, onCancel }) {
  const [testing, setTesting] = useState(false);
  const [showFullForm, setShowFullForm] = useState(!!account);
  const [hostCustom, setHostCustom] = useState(false);
  const [usernameCustom, setUsernameCustom] = useState(false);
  const [oauth2Tokens, setOauth2Tokens] = useState(null);
  const [showOAuth2Config, setShowOAuth2Config] = useState(false);
  const [authType, setAuthType] = useState(null);
  const [oauthAccountStatus, setOauthAccountStatus] = useState(null);
  const isEditing = !!account;

  const {
    availableProviders,
    providerDetails,
    startOAuth2Flow,
    getAccountStatus,
    loading: oauth2Loading,
  } = useOAuth2();

  const { register, handleSubmit, formState: { errors }, getValues, setValue, watch } = useForm({
    defaultValues: {
      name: account?.name || '',
      email: account?.email || '',
      imap_host: account?.imap_host || '',
      imap_port: account?.imap_port || 993,
      imap_tls: account?.imap_tls !== false,
      imap_user: account?.imap_user || '',
      imap_password: '',
      enabled: account?.enabled !== false,
      sync_interval: account?.sync_interval || 300,
      sync_limit: account?.sync_limit || 500,
    },
  });

  const emailValue = watch('email');
  const imapHostValue = watch('imap_host');
  const imapUserValue = watch('imap_user');

  // Parse email and detect provider type
  const emailParts = useMemo(() => {
    if (!emailValue || !emailValue.includes('@')) return null;
    const [username, domain] = emailValue.split('@');
    const domainLower = domain?.toLowerCase();
    const isKnownProvider = !!EMAIL_PROVIDERS[domainLower];
    const domainParts = domainLower.split('.');
    const domainBase = domainParts[0]; // "myowndomain" from "myowndomain.org"

    return {
      username,
      domain: domainLower,
      domainBase,
      isKnownProvider,
      // Suggestions for custom domains
      hostSuggestions: isKnownProvider ? [] : [
        `imap.${domainLower}`,
        `mail.${domainLower}`,
      ],
      usernameSuggestions: isKnownProvider ? [] : [
        emailValue,
        username,
      ],
    };
  }, [emailValue]);

  const createMutation = useCreateAccount();
  const updateMutation = useUpdateAccount();
  const testMutation = useTestAccount();

  const oauth2Provider = emailValue ? detectOAuth2Provider(emailValue) : null;
  const oauth2Configured = oauth2Provider
    ? providerDetails?.[oauth2Provider]?.configured
    : false;

  // Auto-submit when OAuth2 tokens are received (before full form)
  useEffect(() => {
    if (!oauth2Tokens || isEditing || showFullForm) return;

    const email = getValues('email');
    const providerSettings = EMAIL_PROVIDERS[oauth2Tokens.emailDomain];

    const payload = {
      email,
      name: oauth2Tokens.emailDomain
        ? `${oauth2Tokens.emailDomain.split('.')[0]} Account`
        : 'Email Account',
      imap_host: providerSettings?.host || `imap.${oauth2Tokens.emailDomain}`,
      imap_port: providerSettings?.port || 993,
      imap_tls: providerSettings?.tls !== false,
      imap_user: email,
      imap_password: oauth2Tokens.accessToken,
      enabled: true,
      sync_interval: 300,
      sync_limit: 500,
      oauth2: {
        provider: oauth2Tokens.provider,
        accessToken: oauth2Tokens.accessToken,
        refreshToken: oauth2Tokens.refreshToken,
        expiresIn: oauth2Tokens.expiresIn,
      },
    };

    createMutation.mutate(payload, {
      onSuccess: () => onSuccess?.(),
    });
  }, [oauth2Tokens, showFullForm]);

  // Auto-populate IMAP settings based on email domain
  useEffect(() => {
    if (!isEditing && emailParts && showFullForm) {
      // Only set if fields are empty
      const currentHost = getValues('imap_host');
      if (!currentHost) {
        const providerSettings = EMAIL_PROVIDERS[emailParts.domain];

        if (providerSettings) {
          // Known provider
          setValue('imap_host', providerSettings.host);
          setValue('imap_port', providerSettings.port);
          setValue('imap_tls', providerSettings.tls);
          setValue('imap_user', emailValue);
          setValue('name', `${emailParts.domainBase} Account`);
        } else {
          // Custom domain - use first suggestions
          setValue('imap_host', emailParts.hostSuggestions[0] || '');
          setValue('imap_port', 993);
          setValue('imap_tls', true);
          setValue('imap_user', emailParts.usernameSuggestions[0] || '');
          setValue('name', emailParts.domain); // Use full domain like "myowndomain.org"
        }
      }
    }
  }, [emailParts, isEditing, showFullForm, setValue, getValues, emailValue]);

  // Check OAuth2 status when editing an existing account
  useEffect(() => {
    if (!isEditing || !account?.id) return;
    getAccountStatus(account.id).then((status) => {
      setOauthAccountStatus(status);
      if (status?.configured) {
        setAuthType('oauth2');
      }
    }).catch(() => {});
  }, [isEditing, account?.id]);

  const onSubmit = async (data) => {
    try {
      // Transform data - ensure numbers are numbers
      const payload = {
        ...data,
        imap_port: parseInt(data.imap_port),
        sync_interval: parseInt(data.sync_interval),
        sync_limit: parseInt(data.sync_limit),
      };

      if (!isEditing && oauth2Tokens) {
        payload.imap_password = oauth2Tokens.accessToken;
        payload.oauth2 = {
          provider: oauth2Tokens.provider,
          accessToken: oauth2Tokens.accessToken,
          refreshToken: oauth2Tokens.refreshToken,
          expiresIn: oauth2Tokens.expiresIn,
        };
      }

      // Don't send empty password on edit — keep existing server-side
      if (isEditing && !payload.imap_password) {
        delete payload.imap_password;
      }

      if (isEditing) {
        await updateMutation.mutateAsync({ id: account.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      onSuccess?.();
    } catch (err) {
      console.error('Submit error:', err);
      // Error handled by mutation
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const values = getValues();
      await testMutation.mutateAsync({
        imap_host: values.imap_host,
        imap_port: parseInt(values.imap_port),
        imap_tls: values.imap_tls,
        imap_user: values.imap_user,
        imap_password: values.imap_password,
      });
    } catch (err) {
      console.error('Test connection error:', err);
    } finally {
      setTesting(false);
    }
  };

  const handleEmailContinue = () => {
    const email = getValues('email');
    if (email && email.includes('@')) {
      setShowFullForm(true);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Email Address"
        type="email"
        placeholder="user@gmail.com"
        {...register('email', { required: 'Email is required' })}
        error={errors.email?.message}
        autoFocus={!isEditing}
      />

      {!showFullForm && !isEditing && emailValue && emailValue.includes('@') && emailParts && (
        <>
          <p className="text-sm text-muted-foreground">
            {emailParts.isKnownProvider ? (
              <>✓ Detected {emailParts.domainBase} - settings will be pre-filled</>
            ) : (
              <>✓ Custom domain detected - smart suggestions will be provided</>
            )}
          </p>

          {!authType ? (
            <div className="space-y-3">
              <p className="text-sm font-medium">Choose Authentication Method</p>
              <div className="grid grid-cols-2 gap-3">
                {oauth2Provider && (
                  <Button
                    type="button"
                    onClick={() => setAuthType('oauth2')}
                    variant="outline"
                    className="h-auto py-4"
                  >
                    <div className="text-left">
                      <div className="font-medium">OAuth2 (Recommended)</div>
                      <div className="text-xs text-muted-foreground">Secure token-based auth</div>
                    </div>
                  </Button>
                )}
                <Button
                  type="button"
                  onClick={() => setAuthType('password')}
                  variant="outline"
                  className="h-auto py-4"
                >
                  <div className="text-left">
                    <div className="font-medium">Basic Authentication</div>
                    <div className="text-xs text-muted-foreground">IMAP username & password</div>
                  </div>
                </Button>
              </div>
            </div>
          ) : authType === 'oauth2' ? (
            <div className="space-y-3">
              {showOAuth2Config && oauth2Provider && (
                <OAuth2ConfigPrompt
                  provider={oauth2Provider}
                  onConfigured={() => setShowOAuth2Config(false)}
                  onCancel={() => setShowOAuth2Config(false)}
                />
              )}
              {oauth2Tokens ? (
                <div className="p-3 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-sm font-medium text-green-800 dark:text-green-400">
                    ✓ Authenticated with {oauth2Provider === 'google' ? 'Google' : 'Microsoft'}
                  </p>
                </div>
              ) : oauth2Provider && oauth2Configured ? (
                <Button
                  type="button"
                  onClick={async () => {
                    try {
                      const tokens = await startOAuth2Flow(oauth2Provider);
                      setOauth2Tokens({
                        ...tokens,
                        provider: oauth2Provider,
                        emailDomain: emailParts.domain,
                      });
                    } catch (err) {
                      // Error handled by hook
                    }
                  }}
                  loading={oauth2Loading}
                  className="w-full"
                >
                  Authenticate with {oauth2Provider === 'google' ? 'Google' : 'Microsoft'}
                </Button>
              ) : oauth2Provider && !oauth2Configured && !showOAuth2Config ? (
                <Button
                  type="button"
                  onClick={() => setShowOAuth2Config(true)}
                  variant="outline"
                  className="w-full"
                >
                  Configure {oauth2Provider === 'google' ? 'Google' : 'Microsoft'} OAuth2
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setAuthType('password')}
                className="w-full"
              >
                Use Basic Authentication instead
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              onClick={handleEmailContinue}
              className="w-full"
            >
              Continue with Password
            </Button>
          )}
        </>
      )}

      {showFullForm && (
        <>
          <Input
            label="Account Name"
            placeholder="Personal Gmail"
            {...register('name', { required: 'Name is required' })}
            error={errors.name?.message}
          />

      <div className="grid grid-cols-2 gap-4">
        {emailParts && !emailParts.isKnownProvider && !hostCustom ? (
          <Select
            label="IMAP Host"
            {...register('imap_host', { required: 'Host is required' })}
            error={errors.imap_host?.message}
            onChange={(e) => {
              if (e.target.value === '__custom__') {
                setHostCustom(true);
                setValue('imap_host', '');
              }
            }}
          >
            {emailParts.hostSuggestions.map(host => (
              <option key={host} value={host}>{host}</option>
            ))}
            <option value="__custom__">Other (enter manually)</option>
          </Select>
        ) : (
          <Input
            label="IMAP Host"
            placeholder="imap.example.com"
            {...register('imap_host', { required: 'Host is required' })}
            error={errors.imap_host?.message}
          />
        )}

        <Input
          label="IMAP Port"
          type="number"
          placeholder="993"
          {...register('imap_port', { required: 'Port is required' })}
          error={errors.imap_port?.message}
        />
      </div>

      {emailParts && !emailParts.isKnownProvider && !usernameCustom ? (
        <Select
          label="Username"
          {...register('imap_user', { required: 'Username is required' })}
          error={errors.imap_user?.message}
          onChange={(e) => {
            if (e.target.value === '__custom__') {
              setUsernameCustom(true);
              setValue('imap_user', '');
            }
          }}
        >
          {emailParts.usernameSuggestions.map(user => (
            <option key={user} value={user}>{user}</option>
          ))}
          <option value="__custom__">Other (enter manually)</option>
        </Select>
      ) : (
        <Input
          label="Username"
          placeholder="user@example.com"
          {...register('imap_user', { required: 'Username is required' })}
          error={errors.imap_user?.message}
        />
      )}

      {(isEditing && oauthAccountStatus?.configured) || (!isEditing && oauth2Tokens) ? (
        <div className={`p-3 rounded-lg space-y-2 ${
          isEditing && oauthAccountStatus?.isExpired
            ? 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800'
            : 'bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800'
        }`}>
          <p className={`text-sm font-medium ${
            isEditing && oauthAccountStatus?.isExpired
              ? 'text-red-800 dark:text-red-400'
              : 'text-green-800 dark:text-green-400'
          }`}>
            ✓ Authenticated with {oauthAccountStatus?.provider === 'google' || oauth2Tokens?.provider === 'google' ? 'Google' : 'Microsoft'} via OAuth2
          </p>
          {isEditing && oauthAccountStatus?.expiresAt && (
            <p className={`text-xs ${
              oauthAccountStatus.isExpired
                ? 'text-red-700 dark:text-red-500'
                : 'text-green-700 dark:text-green-500'
            }`}>
              {oauthAccountStatus.isExpired ? 'Token expired — re-authorize to continue syncing' : `Token expires ${new Date(oauthAccountStatus.expiresAt * 1000).toLocaleDateString()}`}
            </p>
          )}
          {isEditing && oauthAccountStatus?.isExpired && (
            <Button
              type="button"
              size="sm"
              onClick={async () => {
                try {
                  const provider = oauthAccountStatus?.provider || 'google';
                  const tokens = await startOAuth2Flow(provider);
                  await updateMutation.mutateAsync({
                    id: account.id,
                    data: {
                      oauth2: {
                        provider,
                        accessToken: tokens.accessToken,
                        refreshToken: tokens.refreshToken,
                        expiresIn: tokens.expiresIn,
                      },
                    },
                  });
                  const status = await getAccountStatus(account.id);
                  setOauthAccountStatus(status);
                } catch (err) {
                  // Errors handled by hooks
                }
              }}
              loading={oauth2Loading}
              variant="outline"
              className="w-full"
            >
              Re-authorize with {oauthAccountStatus?.provider === 'google' ? 'Google' : 'Microsoft'}
            </Button>
          )}
        </div>
      ) : (
        <Input
          label={isEditing ? "Password (leave blank to keep current)" : "Password"}
          type="password"
          placeholder="••••••••"
          {...register('imap_password', {
            required: isEditing ? false : 'Password is required',
          })}
          error={errors.imap_password?.message}
        />
      )}

      {(isEditing && oauthAccountStatus?.configured) ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setAuthType('password');
            setOauthAccountStatus(null);
          }}
          className="w-full"
        >
          Switch to Password Authentication
        </Button>
      ) : (!isEditing && authType !== 'oauth2' && oauth2Provider) ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setAuthType('oauth2');
            setShowFullForm(false);
          }}
          className="w-full"
        >
          Switch to OAuth2
        </Button>
      ) : (!isEditing && authType === 'oauth2' && !oauth2Tokens) ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setAuthType('password')}
          className="w-full"
        >
          Use Basic Authentication instead
        </Button>
      ) : null}

      {emailParts?.domain?.match(/hotmail|outlook|live/i) && (
        <div className="p-3 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-400 mb-1">
            ⚠️ Microsoft Account Detected
          </p>
          <p className="text-xs text-yellow-700 dark:text-yellow-500">
            Hotmail/Outlook accounts require an <strong>app-specific password</strong>, not your regular password.
          </p>
          <p className="text-xs text-yellow-700 dark:text-yellow-500 mt-1">
            Generate one at:{' '}
            <a
              href="https://account.microsoft.com/security"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-yellow-900 dark:hover:text-yellow-300"
            >
              account.microsoft.com/security
            </a>
            {' '}→ Advanced security options → App passwords
          </p>
        </div>
      )}

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            {...register('imap_tls')}
            className="h-4 w-4 rounded border-input"
          />
          <span className="text-sm">Use TLS</span>
        </label>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            {...register('enabled')}
            className="h-4 w-4 rounded border-input"
          />
          <span className="text-sm">Enable Syncing</span>
        </label>
      </div>

      <Input
        label="Sync Interval (seconds)"
        type="number"
        placeholder="300"
        {...register('sync_interval', { required: 'Interval is required', min: 60 })}
        error={errors.sync_interval?.message}
      />

      <div>
        <Input
          label="Sync Limit (emails per sync)"
          type="number"
          placeholder="500"
          {...register('sync_limit', { required: 'Limit is required', min: 10, max: 5000 })}
          error={errors.sync_limit?.message}
        />
        <p className="text-xs text-muted-foreground mt-1">
          Maximum emails to fetch per sync. Fetches newest emails first. Default: 500 (higher values may be slow)
        </p>
      </div>

          <div className="flex justify-between pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleTest}
              loading={testing}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              Test Connection
            </Button>

            <div className="flex gap-2">
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {isEditing ? 'Update Account' : 'Create Account'}
              </Button>
            </div>
          </div>
        </>
      )}
    </form>
  );
}
